import {
  useEffect,
  useState
} from 'react';
import './PurchaseProductModal.css';
import {
  AdvisoryContent,
  ContentView,
  PurchaseAction,
  PurchaseViewElement,
  ViewElement
} from '../../../../common/global-types/AdvisoryContent';
import APIUtil from '../../../../common/api/APIUtil';
import useTracking from '../../../../common/tracking/useTracking';

type PurchaseProductModalProps = {
  view: ContentView;
  setViewId: (step: number | undefined) => void;
  proxyPath: string | null;
  setModalMaxWidth: (viewId: number) => void;
  content: AdvisoryContent;
  handleOtherClick: (actionIndex: number) => void;
};

function PurchaseProductModal({
  view,
  setViewId,
  proxyPath,
  setModalMaxWidth,
  content,
  handleOtherClick
}:PurchaseProductModalProps) {
  const [purchaseLoading, setPurchaseLoading] = useState<boolean>(false);
  const [callbackPathTimeoutError, setCallbackPathTimeoutError] = useState<boolean>(false);
  const [unhandledError, setUnhandledError] = useState<boolean>(false);
  const [acceptTermsAndConditions, setAcceptTermsAndConditions] = useState<boolean>(false);
  const [acceptTermsAndConditionsError, setAcceptTermsAndConditionsError] = useState<boolean>(false);  
  const viewElements = view.viewElements as ViewElement;

  const trackVisibilityElement = useTracking(content, view.contentViewId);

  useEffect(() => {
    setModalMaxWidth(1084);
  }, []);

  const acceptTermsAndConditionsEnabled = () => {
    return (viewElements as PurchaseViewElement).acceptTermsAndConditions;
  }

  const handlePurchaseClick = async (actionIndex: number) => {
    if (!viewElements.actions) {
      return;
    }    
    const action = viewElements.actions[actionIndex];
    if (!action.callbackPath) {
      return;
    }

    if (acceptTermsAndConditionsEnabled() && !acceptTermsAndConditions) {
      setAcceptTermsAndConditionsError(true);
      return;
    }

    setPurchaseLoading(true);
    setUnhandledError(false);
    const responseStatus = await APIUtil.handleCallbackUrl(
      proxyPath,
      action.callbackPath,
      10000
    );

    setPurchaseLoading(false);

    if (responseStatus === undefined) {
      setCallbackPathTimeoutError(true);
      return;
    }
    if (responseStatus !== 200) {
      setUnhandledError(true);
      return;
    }

    setViewId(action.loadView!);
  };

  const hasImage = (viewElements.media && viewElements.media.length > 0);

  return (
    <div id="purchase-product" key="purchase-product">
      <div className="head">
        {hasImage && (
        <div className="image-container">
          <img
            src={viewElements.media?.[0].url}
            alt={viewElements.media?.[0].alt}
          />
        </div>
        )}
        <div className={`if tag small ${hasImage ? 'yellow' : 'green'}`}>
          <span style={{ color: !hasImage ? 'var(--primitive-green-400)' : undefined}}>{viewElements.headlines?.[0]}</span>
        </div>
        <h2 className="if typography heading small" ref={trackVisibilityElement}>{viewElements.headlines?.[1]}</h2>
        <ul className="if typography ui small checklist">
          {viewElements.paragraphs?.length > 9
          && viewElements?.paragraphs?.slice(9).map((item) => (
            <li key={item}>
              <span style={{ color: !hasImage ? 'var(--primitive-blue-400)' : undefined }}>
              <ids-icon check ui-16 />
            </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="main">
        <div className="price-summery">
          <div className="if card elevated">
            <div className="if card-content">
              <div className="card-row">
                <span className="if typography ui medium">{viewElements.paragraphs?.[0]}</span>
                <span className="if typography ui medium">{viewElements.paragraphs?.[1]}</span>
              </div>
              <div className="card-row">
                <span className="if typography ui small discount">{viewElements.paragraphs?.[2]}</span>
                <span className="if typography ui small discount">{viewElements.paragraphs?.[3]}</span>
              </div>
              <hr />
              <div className="card-row total-row">
                <span className="if typography ui medium">{viewElements.paragraphs?.[4]}</span>
                <span className="if typography ui medium weight-bold">{viewElements.paragraphs?.[5]}</span>
              </div>
              <span className="if typography ui small year-price">{viewElements.paragraphs?.[6]}</span>
            </div>
          </div>
          <span className="if typography ui x-small payment-method">{viewElements.paragraphs?.[7]}</span>
        </div>
        <a
          href={
            viewElements.actions?.[3]?.url
              ? viewElements.actions[3].url!
              : undefined
            }
          target={
            viewElements.actions?.[3]?.target
              ? viewElements.actions[3].target!
              : undefined
            }
          className="discount-offer"
          style={{ textDecoration: 'none' }}
        >
          <div className="if card filled" onClick={() => handleOtherClick(3)}>
            <div className="if card-content narrow">
              {viewElements.icons?.[0]?.name && viewElements.icons?.[0]?.group && (
                <div>
                  <ids-icon {...{ [viewElements.icons[0].name]: true, [`${viewElements.icons[0].group}-24`]: true }} />
                </div>
              )}
              <div className="if">
                <span className="if typography ui small">
                  {viewElements.actions?.[3]?.text}
                </span>
              </div>
              <div className="if chevron-icon-wrapper">
                {viewElements.actions?.[3]?.idsIconName && viewElements.actions?.[3]?.idsIconGroup && (
                  <ids-icon
                    {...{
                      [viewElements.actions[3].idsIconName]: true,
                      [`${viewElements.actions[3].idsIconGroup}-16`]: true
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </a>
        <details className="if expandable-section light terms-conditions">
          {viewElements.actions && viewElements.actions?.length > 4 && (
            <summary id="title-1" className="if expandable-section-title" aria-controls="content-1" onClick={() => handleOtherClick(4)}>
              <span>{viewElements.actions[4].text}</span>
              <span id="expandable-section-arrow" className="if expandable-section-arrow">
                <ids-icon
                  {...{
                    [viewElements.actions[4].idsIconName!]: true,
                    [`${viewElements.actions[4].idsIconGroup}-24`]: true
                  }}
                />
              </span>
            </summary>
          )}
          <div id="content-1" role="region" aria-labelledby="title-1" className="if expandable-section-content">
            {viewElements.actions && viewElements.actions?.length > 5
              && viewElements.actions.slice(5).map((link, index) => (
                <a
                  href={link.url!}
                  target={link.target ?? '_blank'}
                  className="if link standalone icon"
                  rel="noopener noreferrer"
                  key={link.text!}
                  onClick={() => handleOtherClick(index)}
                >
                  {link.text}
                  <ids-icon {...{ [link.idsIconName!]: true, [`${link.idsIconGroup}-16`]: true }} />
                </a>
              ))}
          </div>
        </details>
        {acceptTermsAndConditionsEnabled() && (
          <div className="accept-terms-and-conditions-container">
            <div className="if checkbox">
                <input
                  aria-describedby={ acceptTermsAndConditionsError ? 'accept-terms-and-condition-error-message' : undefined }
                  aria-invalid="true"
                  className={`if checkbox-input ${acceptTermsAndConditionsError ? 'is-invalid' : undefined}`}
                  type="checkbox"
                  id="accept-terms-and-conditions"
                  name="accept-terms-and-conditions"
                  checked={acceptTermsAndConditions}
                  onChange={(e) => {
                    if (acceptTermsAndConditionsError && e.target.checked) {
                      setAcceptTermsAndConditionsError(false);
                    }
                    setAcceptTermsAndConditions(e.target.checked);
                  }}
                />
                <label className="if typography paragraph small" htmlFor="accept-terms-and-conditions">
                  {acceptTermsAndConditionsEnabled()!.text}
                </label>
            </div>
            {acceptTermsAndConditionsError && (
              <span
                aria-atomic="true"
                aria-live="assertive"
                className="if error-message typography paragraph small"
                id="accept-terms-and-condition-error-message"
              >
                {acceptTermsAndConditionsEnabled()!.errorMessage}
              </span>
            )}
          </div>
        )}
        <p className="if typography ui small">{viewElements.paragraphs[8]}</p>
        <div className="action-buttons">
          {viewElements.actions?.[1] && (
            <button
              className={viewElements.actions[1].idsClassName || undefined}
              type="button"
              onClick={() => handlePurchaseClick(1)}
              disabled={purchaseLoading || callbackPathTimeoutError || unhandledError || acceptTermsAndConditionsError}
            >
              {(viewElements.actions[1].text)}
              {purchaseLoading && <span className="if loader dots x-small" />}
            </button>
          )}
          {callbackPathTimeoutError && (
            <span className="if error-message">
              {(viewElements.actions?.[1] as PurchaseAction).purchaseTimeoutErrorMessage}
            </span>
          )}
          {unhandledError && (
            <span className="if error-message">
              {(viewElements.actions?.[1] as PurchaseAction).purchaseUnhandledErrorMessage}
            </span>
          )}
          {viewElements.actions && viewElements.actions?.[2] && (
            <button
              className={viewElements.actions[2].idsClassName || undefined}
              type="button"
              onClick={() => {
                handleOtherClick(2);
                setViewId(viewElements.actions![2].loadView!);
              }}
              disabled={purchaseLoading || callbackPathTimeoutError || unhandledError}
            >
              {viewElements.actions[2].text}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default PurchaseProductModal;
